import { useMemo } from 'react';

import { getVisibleCommentThreads } from '@confluence/comments-panel-utils/entry-points/getVisibleCommentThreads';
import {
	LocationValue,
	getOpenCommentThreads,
	getAnnotationThreadKeys,
} from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import { fg } from '@confluence/feature-gating';
import {
	useCommentsDataState,
	type CommentsDataMap as LegacyCommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData';
import {
	useAllCommentsData,
	buildDefaultCommentsContentStatusFilters,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import { expVal } from '@confluence/feature-experiments';

export const useFooterComments = ({ contentId }: { contentId?: string } = {}) => {
	const {
		orderedActiveAnnotationIdList,
		commentsDataMap: legacyCommentsDataMap,
		removedThreadsMap,
	} = useCommentsDataState();

	// New comments data manager - choose which commentsDataMap to use
	const newCommentsDataResult = useAllCommentsData({
		contentId: contentId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(),
		options: {
			skip: !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) || !contentId,
		},
	});

	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? (newCommentsDataResult.commentsDataMap as LegacyCommentsDataMap)
		: legacyCommentsDataMap;

	return useMemo(() => {
		const footerCommentThreads = fg('confluence_frontend_content_wrapper')
			? getVisibleCommentThreads({
					commentsDataMap,
					includeGeneralComments: true,
					includeInlineComments: false,
					orderedAnnotationIds: getAnnotationThreadKeys(orderedActiveAnnotationIdList),
				})
			: getOpenCommentThreads({
					commentsDataMap,
					showInlineComments: false,
					showGeneralComments: true,
					location: LocationValue.FOOTER,
					orderedActiveAnnotationIdList,
					removedThreadsMap,
				});

		const footerCommentIds = footerCommentThreads.map((thread) => thread.id);

		return {
			footerCommentIds,
			footerCommentThreads,
		};
	}, [commentsDataMap, orderedActiveAnnotationIdList, removedThreadsMap]);
};
